import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Section, Toggle } from '../PageShell.tsx';
import { api } from '../../lib/api.ts';
import { SaveRow, useSaved } from './shared.tsx';
import type { SettingsDto } from './shared.tsx';

/** A few that look right on a launcher, so most owners never open the picker. */
const PRESET_COLORS = ['#7958f8', '#2563eb', '#0891b2', '#059669', '#ea580c', '#dc2626', '#1f2937'];


export function WidgetTab({
  settings,
  isOwner,
}: {
  settings: SettingsDto;
  isOwner: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [integration, setIntegration] = useState(settings.chat.integration);
  const [copied, setCopied] = useState(false);
  const [saved, markSaved] = useSaved();

  const save = useMutation({
    mutationFn: () => api.put('/settings/integration', integration),
    onSuccess: () => {
      markSaved();
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  // With neither field the widget's pre-chat form has nothing to collect, so a
  // visitor could never open a conversation. The server rejects it too; this
  // stops the owner reaching a dead configuration in the first place.
  const noContactField = !integration.askEmail && !integration.askPhone;

  return (
    <>
      <Section
        title="Install the widget"
        description="Paste this before the closing </body> tag on every page."
      >
        <pre className="lg-scroll overflow-x-auto rounded-lg bg-slate-900 px-4 py-3 text-xs text-slate-100 dark:bg-slate-950">
          <code>{settings.widget.snippet}</code>
        </pre>

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            className="lg-btn-secondary"
            onClick={() => {
              void navigator.clipboard.writeText(settings.widget.snippet).then(() => {
                setCopied(true);
                window.setTimeout(() => {
                  setCopied(false);
                }, 2000);
              });
            }}
          >
            {copied ? 'Copied' : 'Copy snippet'}
          </button>
        </div>
      </Section>

      <Section
        title="What the widget asks for"
        description={
          isOwner
            ? 'A customer must leave at least one way to reach them, so keep at least one of these on.'
            : 'Only the workspace owner can change these.'
        }
      >
        <fieldset disabled={!isOwner} className="space-y-3">
          <Toggle
            label="Show the chat bubble"
            description="Turn off to open the widget only from your own buttons."
            checked={integration.showChatIcon}
            onChange={(value) => {
              setIntegration((previous) => ({ ...previous, showChatIcon: value }));
            }}
          />
          <Toggle
            label="Ask for an email address"
            checked={integration.askEmail}
            onChange={(value) => {
              setIntegration((previous) => ({ ...previous, askEmail: value }));
            }}
          />
          <Toggle
            label="Ask for a phone number"
            description="Needed for SMS follow-up."
            checked={integration.askPhone}
            onChange={(value) => {
              setIntegration((previous) => ({ ...previous, askPhone: value }));
            }}
          />

          <div className="border-t border-slate-200 pt-3 dark:border-slate-800">
            <label className="lg-label" htmlFor="widget-color">
              Widget colour
            </label>

            <div className="flex flex-wrap items-center gap-2">
              {/* Two controls over one value: the swatch for picking, the text
                  box for pasting the hex a brand guide already specifies. */}
              <input
                id="widget-color"
                type="color"
                className="h-9 w-14 cursor-pointer rounded-lg border border-slate-300 bg-transparent p-1 dark:border-slate-700"
                value={integration.widgetColor}
                onChange={(event) => {
                  setIntegration((previous) => ({ ...previous, widgetColor: event.target.value }));
                }}
              />

              <input
                className="lg-input w-32 font-mono"
                value={integration.widgetColor}
                onChange={(event) => {
                  setIntegration((previous) => ({ ...previous, widgetColor: event.target.value }));
                }}
              />

              {PRESET_COLORS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  aria-label={`Use ${preset}`}
                  className="h-7 w-7 rounded-full border border-black/10 transition hover:scale-110 dark:border-white/20"
                  style={{ backgroundColor: preset }}
                  onClick={() => {
                    setIntegration((previous) => ({ ...previous, widgetColor: preset }));
                  }}
                />
              ))}
            </div>

            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              The header, the launcher and the send button. The darker shade for
              hover is worked out from it.
            </p>

            <div
              className="mt-3 flex items-center gap-3 rounded-lg p-3"
              style={{ backgroundColor: integration.widgetColor }}
            >
              <span className="text-sm font-semibold text-white">Preview</span>
              <span className="text-xs text-white/80">how the widget header will read</span>
            </div>
          </div>
        </fieldset>

        {isOwner ? (
          <SaveRow
            pending={save.isPending}
            saved={saved}
            disabled={noContactField}
            error={
              noContactField
                ? 'Ask for an email address, a phone number, or both — otherwise nobody can start a chat.'
                : null
            }
            onSave={() => {
              save.mutate();
            }}
          />
        ) : null}
      </Section>
    </>
  );
}



/** The message from a failed save, if the failure carried one. */
